"use client";

import { useEffect, useLayoutEffect, useRef } from "react";

import { PURRING_CAT_CSS, PURRING_CAT_SVG } from "./purringCatArt";

// useLayoutEffect warns during server rendering; the page cat is server-rendered.
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Where the page cat's 7.5 s loop started (performance.now() timebase), so the
 * card cat can pick up her walk at the same point instead of restarting it.
 */
let catLoopStart: number | null = null;

/** Test hook: forget any recorded loop. */
export const __resetCatLoopForTest = () => {
  catLoopStart = null;
};

type Animatable = {
  currentTime: number | null;
  effect?: { getTiming?: () => { iterations?: number } } | null;
};
/** True for the drawing's looping animations (the walk etc.), not the one-off fade-in. */
const loops = (a: Animatable) => a.effect?.getTiming?.().iterations === Infinity;
const animationsIn = (el: Element | null): Animatable[] => {
  const get = (el as unknown as { getAnimations?: (o: object) => Animatable[] })
    ?.getAnimations;
  return typeof get === "function" ? get.call(el, { subtree: true }) : [];
};

export const PURR_CAPTION = "Purrfecting your eCard…";

/**
 * She waits this long, then fades in. On a fast line the card is ready before
 * that, so the cat never flashes up and vanishes (Laura, 2026-10-18). Pure CSS,
 * so it works in the server HTML before any JS; the handover carries the timing
 * over to the cat inside the card.
 */
export const CAT_APPEAR_DELAY_MS = 300;
export const CAT_FADE_IN_MS = 150;

/**
 * Has the page cat faded in yet? Decides whether the hand-off to the card dips
 * to white (she was on screen) or simply gets out of the way (a fast load that
 * never showed her).
 */
export const catHasAppeared = () =>
  catLoopStart !== null && performance.now() - catLoopStart >= CAT_APPEAR_DELAY_MS;

/**
 * One layout wherever she is drawn — the page frame or the 1080-wide card — so
 * the two cats overlap exactly during the crossfade: the caption is a fixed
 * fraction of her own width (44px on the 1080-wide card ≈ 4.07%).
 */
const LAYOUT_CSS = `
.wgc-root { container-type: inline-size; }
.wgc-caption { font-size: 4.07cqw; }
`;

/**
 * Each paw print declares its animation inline (per-print timing), which beats
 * the drawing's own reduced-motion rule; this override outranks it so the
 * prints sit still for people who ask for less motion (Copilot on #791).
 */
const REDUCED_MOTION_CSS = `
@media (prefers-reduced-motion: reduce) { .wgc-c-print { animation: none !important; opacity: 1 !important; transform: none !important; } }
`;

const APPEAR_CSS = `
.wgc-root { animation: wgc-appear ${CAT_FADE_IN_MS}ms ease-out ${CAT_APPEAR_DELAY_MS}ms both; }
.wgc-root.wgc-no-appear { animation: none; }
@keyframes wgc-appear { from { opacity: 0; } to { opacity: 1; } }
`;

/**
 * What a recipient sees while a card's media load (#791).
 *
 * A tuxedo cat steps off a paint palette, walks across a painting leaving
 * rainbow paw prints, curls up in the corner and purrs. Designed with Laura,
 * 2026-10-17.
 *
 * Drawn by BufferingCatOverlay while the Player is buffering — held by
 * HoldPlaybackUntilAssetsLoaded, or by a <Video>/<Audio>/<Img> still loading —
 * and only after 300ms, so a fast load never shows her.
 *
 * The drawing is static inline SVG animated with CSS: a few KB, sharp at any
 * size, no network request of its own — it must appear while the network is
 * the thing being waited on.
 */
export const PurringCatLoader = ({
  handover,
  appear = true,
}: {
  /**
   * Two cats, one walk. "record": the page-level cat (before the Player mounts)
   * notes how far into her loop she is once the page hydrates. "resume": the cat
   * inside the card jumps her animations to that point, so the walk continues
   * rather than restarting at the handover.
   */
  handover?: "record" | "resume";
  /**
   * Fade in after CAT_APPEAR_DELAY_MS (default). Pass false where the caller has
   * already waited, e.g. a later stall that waited 300ms in code.
   */
  appear?: boolean;
}) => {
  const root = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (handover === "record") {
      // Read the clock from a LOOPING animation. The root's fade-in finishes at
      // 450ms and its clock stops there; on a slow hydration it would put the
      // card cat behind the walk, and she'd jump backwards (Copilot on #791).
      const anims = animationsIn(root.current);
      const running =
        anims.find((a) => loops(a) && typeof a.currentTime === "number") ??
        anims.find((a) => typeof a.currentTime === "number");
      if (running && typeof running.currentTime === "number") {
        catLoopStart = performance.now() - running.currentTime;
      }
    } else if (handover === "resume" && catLoopStart !== null) {
      const elapsed = performance.now() - catLoopStart;
      for (const a of animationsIn(root.current)) a.currentTime = elapsed;
    }
  }, [handover]);

  return (
  <div
    ref={root}
    className={appear ? "wgc-root" : "wgc-root wgc-no-appear"}
    role="status"
    aria-live="polite"
    style={{
      position: "absolute",
      inset: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "4%",
      background: "#ffffff",
    }}
  >
    <style>{PURRING_CAT_CSS + LAYOUT_CSS + APPEAR_CSS + REDUCED_MOTION_CSS}</style>
    <div
      aria-hidden="true"
      style={{ width: "82%" }}
      // Static markup generated from the approved design; see purringCatArt.ts.
      dangerouslySetInnerHTML={{ __html: PURRING_CAT_SVG }}
    />
    <p className="wgc-caption">{PURR_CAPTION}</p>
  </div>
  );
};
